export const GROUPS = [
  { id: "costs", label: "Project Costs" },
  { id: "financing", label: "Financing" },
  { id: "operations", label: "Operations" },
  { id: "exit", label: "Exit" },
];

const C = "currency", P = "pct", I = "int";

export const FIELDS = [
  { key: "land", label: "Land / Acquisition", group: "costs", type: C, min: 0, max: 30000000, step: 100000 },
  { key: "hard", label: "Hard Construction Costs", group: "costs", type: C, min: 0, max: 70000000, step: 250000 },
  { key: "soft", label: "Soft Costs", group: "costs", type: C, min: 0, max: 15000000, step: 50000 },
  { key: "ffe", label: "FF&E", group: "costs", type: C, min: 0, max: 12000000, step: 50000 },
  { key: "contingency_pct", label: "Contingency (% of hard + soft)", group: "costs", type: P, min: 0, max: 20, step: 0.5 },
  { key: "preopening", label: "Pre-Opening Costs", group: "costs", type: C, min: 0, max: 3000000, step: 25000 },
  { key: "ltc_pct", label: "Loan-to-Cost", group: "financing", type: P, min: 0, max: 85, step: 0.5 },
  { key: "interest_rate", label: "Interest Rate", group: "financing", type: P, min: 2, max: 14, step: 0.05 },
  { key: "loan_term", label: "Loan Term (years)", group: "financing", type: I, min: 1, max: 30, step: 1 },
  { key: "amortization", label: "Amortization (years)", group: "financing", type: I, min: 5, max: 40, step: 1 },
  { key: "io_years", label: "Interest-Only Period (years)", group: "financing", type: I, min: 0, max: 10, step: 1 },
  { key: "rooms", label: "Number of Rooms", group: "operations", type: I, min: 25, max: 400, step: 1 },
  { key: "adr", label: "Stabilized ADR (Year-1 dollars)", group: "operations", type: C, min: 75, max: 600, step: 1 },
  { key: "occupancy", label: "Occupancy", group: "operations", type: P, min: 30, max: 95, step: 0.5 },
  { key: "adr_growth", label: "Annual ADR Growth", group: "operations", type: P, min: 0, max: 8, step: 0.1 },
  { key: "other_rev_pct", label: "F&B + Other Revenue (% of rooms)", group: "operations", type: P, min: 0, max: 100, step: 0.5 },
  { key: "opex_pct", label: "Operating Expenses (% of revenue)", group: "operations", type: P, min: 20, max: 85, step: 0.5 },
  { key: "mgmt_fee_pct", label: "Management Fee (% of revenue)", group: "operations", type: P, min: 0, max: 8, step: 0.1 },
  { key: "ffe_reserve_pct", label: "FF&E Reserve (% of revenue)", group: "operations", type: P, min: 0, max: 8, step: 0.1 },
  { key: "property_taxes", label: "Property Taxes (Year 1)", group: "operations", type: C, min: 0, max: 3000000, step: 10000 },
  { key: "insurance", label: "Insurance (Year 1)", group: "operations", type: C, min: 0, max: 3000000, step: 10000 },
  { key: "ramp_y1_pct", label: "Ramp-Up: Year 1 Occupancy (% of stabilized)", group: "operations", type: P, min: 50, max: 100, step: 1 },
  { key: "ramp_y2_pct", label: "Ramp-Up: Year 2 Occupancy (% of stabilized)", group: "operations", type: P, min: 50, max: 100, step: 1 },
  { key: "ramp_adr_y1_pct", label: "Ramp-Up: Year 1 ADR (% of stabilized)", group: "operations", type: P, min: 50, max: 100, step: 1 },
  { key: "hold_years", label: "Hold Period (years)", group: "exit", type: I, min: 1, max: 15, step: 1 },
  { key: "exit_cap", label: "Exit Cap Rate", group: "exit", type: P, min: 4, max: 11, step: 0.05 },
  { key: "selling_costs_pct", label: "Selling Costs", group: "exit", type: P, min: 0, max: 6, step: 0.1 },
];

export function irr(cfs) {
  const npv = (r) => cfs.reduce((a, c, t) => a + c / Math.pow(1 + r, t), 0);
  let lo = -0.99, hi = 2;
  let flo = npv(lo);
  const fhi = npv(hi);
  if (!Number.isFinite(flo) || !Number.isFinite(fhi) || flo * fhi > 0) return null;
  for (let k = 0; k < 200; k++) {
    const mid = (lo + hi) / 2;
    const fm = npv(mid);
    if (Math.abs(fm) < 1e-7) return mid;
    if (flo * fm < 0) hi = mid;
    else { lo = mid; flo = fm; }
  }
  return (lo + hi) / 2;
}

const P_ = (v) => (Number(v) || 0) / 100;
const ramp = (i, y) => (y === 1 ? P_(i.ramp_y1_pct ?? 100) : y === 2 ? P_(i.ramp_y2_pct ?? 100) : 1);
const stabilizedYear = () => 3;

function operatingYear(i, y, rooms) {
  const f = Math.pow(1 + P_(i.adr_growth), y - 1);
  const adr = i.adr * f * (y === 1 ? P_(i.ramp_adr_y1_pct ?? 100) : 1);
  const occ = P_(i.occupancy) * ramp(i, y);
  const roomRev = rooms * 365 * occ * adr;
  const otherRev = roomRev * P_(i.other_rev_pct);
  const totalRev = roomRev + otherRev;
  const opex = totalRev * P_(i.opex_pct);
  const mgmt = totalRev * P_(i.mgmt_fee_pct);
  const reserve = totalRev * P_(i.ffe_reserve_pct);
  const taxes = i.property_taxes * f;
  const ins = i.insurance * f;
  const noi = totalRev - opex - mgmt - reserve - taxes - ins;
  return { year: y, adr, occ, revpar: adr * occ, roomRev, otherRev, totalRev, opex, mgmt, reserve, taxes, ins, noi };
}

export function compute(i) {
  const rooms = Math.max(1, Math.round(i.rooms));
  const hold = Math.max(1, Math.round(i.hold_years));
  const contingency = (i.hard + i.soft) * P_(i.contingency_pct);
  const totalCost = i.land + i.hard + i.soft + i.ffe + contingency + i.preopening;
  const loan = totalCost * P_(i.ltc_pct);
  const equity = totalCost - loan;
  const r = P_(i.interest_rate);
  const mr = r / 12;
  const n = Math.max(1, Math.round(i.amortization)) * 12;
  const pmt = loan > 0 ? (mr === 0 ? loan / n : (loan * mr) / (1 - Math.pow(1 + mr, -n))) : 0;

  let bal = loan;
  const years = [];
  for (let y = 1; y <= hold; y++) {
    const op = operatingYear(i, y, rooms);
    let interest = 0, principal = 0;
    if (bal > 0) {
      if (y <= i.io_years) interest = bal * r;
      else {
        for (let m = 0; m < 12; m++) {
          const it = bal * mr;
          const pr = Math.min(pmt - it, bal);
          interest += it; principal += pr; bal -= pr;
        }
      }
    }
    const ds = interest + principal;
    years.push({ ...op, interest, principal, ds, cfAfter: op.noi - ds, dscr: ds > 0 ? op.noi / ds : null, balance: bal });
  }
  const forward = operatingYear(i, hold + 1, rooms);
  const cap = P_(i.exit_cap);
  const exitValue = cap > 0 && forward.noi > 0 ? forward.noi / cap : 0;
  const sellingCosts = exitValue * P_(i.selling_costs_pct);
  const netSale = exitValue - sellingCosts;
  const payoff = bal;

  const unlev = [-totalCost, ...years.map((y) => y.noi)];
  unlev[hold] += netSale;
  const lev = [-equity, ...years.map((y) => y.cfAfter)];
  lev[hold] += netSale - payoff;

  let cum = -equity;
  const cumulative = [{ year: "Y0", cumulative: cum }];
  lev.slice(1).forEach((c, k) => { cum += c; cumulative.push({ year: `Y${k + 1}`, cumulative: cum }); });

  const distributions = lev.slice(1).reduce((a, b) => a + b, 0);
  const dscrs = years.map((y) => y.dscr).filter((d) => d !== null);
  const y1 = years[0];
  const stabYear = stabilizedYear(i);
  const stab = operatingYear(i, stabYear, rooms);

  return {
    rooms, hold, contingency, totalCost, costPerKey: totalCost / rooms, loan, equity, years, forwardNoi: forward.noi,
    exitValue, exitPerKey: exitValue / rooms, sellingCosts, netSale, payoff, unlevCF: unlev, levCF: lev, cumulative,
    unleveredIRR: irr(unlev), leveredIRR: equity > 0 ? irr(lev) : null,
    equityMultiple: equity > 0 ? distributions / equity : null,
    coc1: equity > 0 ? y1.cfAfter / equity : null,
    avgCoc: equity > 0 ? years.reduce((a, y) => a + y.cfAfter, 0) / years.length / equity : null,
    dscr1: y1.dscr, minDscr: dscrs.length ? Math.min(...dscrs) : null,
    stabYear, stabNoi: stab.noi,
    yieldOnCost: totalCost > 0 ? stab.noi / totalCost : null,
    devSpread: totalCost > 0 ? stab.noi / totalCost - cap : null,
    revpar1: y1.revpar, totalRev1: y1.totalRev, noi1: y1.noi,
    costBreakdown: [
      { name: "Land / Acquisition", value: i.land },
      { name: "Hard Costs", value: i.hard },
      { name: "Soft Costs", value: i.soft },
      { name: "FF&E", value: i.ffe },
      { name: "Contingency", value: contingency },
      { name: "Pre-Opening", value: i.preopening },
    ].filter((c) => c.value > 0),
  };
}

export function sensitivity(i) {
  const caps = [-1, -0.5, 0, 0.5, 1].map((d) => Math.max(0.5, +(i.exit_cap + d).toFixed(2)));
  const occs = [-10, -5, 0, 5, 10].map((d) => Math.min(100, Math.max(1, +(i.occupancy + d).toFixed(1))));
  const grid = occs.map((o) => caps.map((c) => compute({ ...i, occupancy: o, exit_cap: c }).leveredIRR));
  return { caps, occs, grid };
}

const costUp = (i) => ({ ...i, hard: Math.round(i.hard * 1.1), soft: Math.round(i.soft * 1.1), ffe: Math.round(i.ffe * 1.1) });
const capUp = (i) => ({ ...i, exit_cap: +(i.exit_cap + 0.5).toFixed(2) });
const slowRamp = (i) => ({ ...i, ramp_y1_pct: Math.max(50, (i.ramp_y1_pct ?? 100) - 10), ramp_y2_pct: Math.max(50, (i.ramp_y2_pct ?? 100) - 5) });

export const STRESS = [
  { id: "cost", label: "Construction cost +10%", desc: "Hard, soft and FF&E costs × 1.10", apply: costUp },
  { id: "cap", label: "Exit cap +50 bps", desc: "Exit cap rate + 0.50%", apply: capUp },
  { id: "ramp", label: "Slower ramp-up", desc: "Year 1 ramp −10 pts, Year 2 ramp −5 pts", apply: slowRamp },
  { id: "combined", label: "All three combined", desc: "Cost +10%, cap +50 bps and slower ramp-up", apply: (i) => slowRamp(capUp(costUp(i))) },
];
